import React, { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  UserPlus,
} from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { SearchableSelect } from "./ui/searchable-select";
import { Button } from "./ui/button";

function isEmptyEmployeeRow(row) {
  if (!row) return true;
  if (row.isAdding || row.isNew) return false;
  const employeeId = String(row.employeeId || "").trim();
  const name = String(row.name || "").trim();
  const role = String(row.role || "").trim();
  const hours = String(row.hours || "").trim();
  const minutes = String(row.minutes || "").trim();
  const rate = String(row.rate || "").trim();
  return !employeeId && !name && !role && !hours && !minutes && !rate;
}

function money(n) {
  const num = Number(n) || 0;
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function sanitizeNumberInput(raw) {
  if (raw === "") return "";
  const cleaned = String(raw).replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("")}`;
}

function sanitizeIntInput(raw) {
  if (raw === "") return "";
  return String(raw).replace(/\D/g, "");
}

function normalizeHoursMinutes(hoursRaw, minutesRaw) {
  const hoursInt = hoursRaw === "" ? 0 : parseInt(String(hoursRaw), 10);
  const minutesInt = minutesRaw === "" ? 0 : parseInt(String(minutesRaw), 10);

  const safeHours = Number.isFinite(hoursInt) && hoursInt >= 0 ? hoursInt : 0;
  const safeMinutes =
    Number.isFinite(minutesInt) && minutesInt >= 0 ? minutesInt : 0;

  const carryHours = Math.floor(safeMinutes / 60);
  const finalHours = safeHours + carryHours;
  const finalMinutes = safeMinutes % 60;

  return { hours: String(finalHours), minutes: String(finalMinutes) };
}

function clampNonNegativeIntString(raw) {
  if (raw === "") return "";
  const n = parseInt(String(raw), 10);
  if (Number.isNaN(n) || n < 0) return "0";
  return String(n);
}

function SpinnerControls({ onUp, onDown, disabled }) {
  return (
    <div className="ml-2 flex flex-col">
      <ChevronUp className="h-4 w-4 cursor-pointer" onClick={onUp} disabled={disabled} />
      <ChevronDown className="h-4 w-4 cursor-pointer" onClick={onDown} disabled={disabled} />
    </div>
  );
}

export default function Step2ReviewLines({
  rows,
  setRows,
  errors,
  employeeOptions,
  employeeFetchError,
  employeeById,
  onRowsChanged,
}) {
  const displayRows = useMemo(() => {
    const arr = Array.isArray(rows) ? rows : [];
    return arr.filter((r) => !isEmptyEmployeeRow(r));
  }, [rows]);

  const pruneEmptyRows = () => {
    setRows((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const next = arr.filter((r) => !isEmptyEmployeeRow(r));
      return next.length === arr.length ? prev : next;
    });
  };

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  /** Parent totals exclude rows with isNew: true — commit any line that has an employee picked. */
  const commitRowsWithSelectedEmployee = (prev) => {
    const arr = Array.isArray(prev) ? prev : [];
    return arr.map((r) => {
      const hasEmployee = String(r?.employeeId || "").trim() !== "";
      if (!hasEmployee || !r?.isNew) return r;
      return { ...r, isNew: false, isAdding: false };
    });
  };

  const addRow = () => {
    const newRow = {
      id: crypto.randomUUID(),
      employeeId: "",
      name: "",
      role: "",
      hours: "",
      minutes: "",
      rate: "",
      isAdding: true,
    };
    setRows((prev) => [...commitRowsWithSelectedEmployee(prev), newRow]);
    if (typeof onRowsChanged === "function") {
      onRowsChanged();
    }
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    if (typeof onRowsChanged === "function") {
      onRowsChanged();
    }
  };

  return (
    <Card className="border-[#2F7F571A] shadow-[0px_1px_2px_0px_#0000000D] overflow-hidden">
      <div className="p-6 bg-[#2F7F570D] border-b border-[#2F7F570D] border">
        <div className="flex md:flex-row flex-col gap-5 items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-[#2F7F57] flex items-center justify-center text-white text-base font-bold flex-shrink-0 mt-0.5">
              2
            </div>
            <div>
              <div className="text-md font-bold text-[#131614]">Line Items Review</div>
              <div className="text-xs text-[#6B7280]">Review auto-populated staff costs and maintenance</div>
            </div>
          </div>
          <Button
            onClick={addRow}
            className="flex items-center gap-1.5 text-[#2F7F57] bg-transparent hover:bg-[#2F7F57] hover:text-white transition-colors"
          >
            <UserPlus className="h-4 w-4" /> Add Employee
          </Button>
        </div>
      </div>

      <CardContent className="pt-6">
        {errors?.form && <div className="text-xs text-red-600 mb-3">{errors.form}</div>}
        {employeeFetchError && <div className="text-xs text-red-600 mb-3">{employeeFetchError}</div>}

        <div className="text-xs font-bold text-[#2F7F57] tracking-widest mb-4">STAFF & MAINTENANCE PERSONNEL</div>

        <div className="rounded-xl border border-[#2F7F571A] overflow-hidden">
          <div className="w-full overflow-x-auto">
            <div className="min-w-[820px]">
              <div className="grid grid-cols-[repeat(13,minmax(0,1fr))] gap-x-4 bg-[#2F7F570D] border-b border-[#2F7F571A] text-xs font-bold text-[#6B7280] uppercase">
                <div className="col-span-1 p-3">Employee ID</div>
                <div className="col-span-3 p-3">Employee Name</div>
                <div className="col-span-2 p-3">Hours</div>
                <div className="col-span-2 p-3">Minutes</div>
                <div className="col-span-2 p-3">Rate ($)</div>
                <div className="col-span-2 p-3">Total</div>
                <div className="col-span-1 p-3 pl-0 pr-1 whitespace-nowrap">Action</div>
              </div>
              {displayRows.map((row) => {
                const errIdx = (Array.isArray(rows) ? rows : []).findIndex((r) => r.id === row.id);
                const h = parseInt(row.hours, 10) || 0;
                const m = parseInt(row.minutes, 10) || 0;
                const totalHours = h + m / 60;
                const rate = parseFloat(row.rate) || 0;
                const amt = totalHours * rate;
                return (
                  <div
                    key={row.id}
                    className="grid grid-cols-[repeat(13,minmax(0,1fr))] gap-x-4 border-b border-[#2F7F571A] last:border-b-0 items-center text-[#6B7280]"
                  >
                    <div className="col-span-1 p-3 pt-0 text-sm">{row.employeeId ? `#${row.employeeId}` : "—"}</div>
                    <div className="col-span-3 py-3 pt-5 pb-2">
                      <div className="flex flex-col justify-center min-w-0 h-full space-y-2">
                        <SearchableSelect
                          value={row.employeeId || ""}
                          displayOverride={row.name}
                          onValueChange={(val) => {
                            const selected = (employeeOptions || []).find((o) => String(o.value) === String(val));
                            const emp = employeeById?.[String(val)];
                            const payRate = emp?.payRate ?? emp?.hourlyRate;
                            updateRow(row.id, {
                              employeeId: val,
                              name: selected?.label || "",
                              rate: payRate != null && payRate !== "" ? String(payRate) : row.rate,
                              isAdding: false,
                              isNew: false,
                            });
                            pruneEmptyRows();
                          }}
                          options={employeeOptions || []}
                          placeholder="Select employee"
                          className="w-full justify-start text-black"
                        />
                        <div className="text-[10px] text-red-600 min-h-[14px]">
                          {errors?.rows?.[errIdx]?.employeeId || errors?.rows?.[errIdx]?.name || ""}
                        </div>
                      </div>
                    </div>

                    <div className="col-span-2 py-3 pt-5 pb-2 text-primary">
                      <div className="flex flex-col w-full h-full">
                        <div className="flex items-center">
                          <Input
                            value={row.hours}
                            onChange={(e) => {
                              const hoursNext = sanitizeIntInput(e.target.value);
                              const normalized = normalizeHoursMinutes(hoursNext, row.minutes);
                              updateRow(row.id, { hours: normalized.hours, minutes: normalized.minutes });
                              pruneEmptyRows();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addRow();
                              }
                            }}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="0"
                            className="text-black w-full"
                          />
                          <SpinnerControls
                            disabled={false}
                            onUp={() => {
                              const next = clampNonNegativeIntString((parseInt(row.hours || "0", 10) || 0) + 1);
                              const normalized = normalizeHoursMinutes(next, row.minutes);
                              updateRow(row.id, { hours: normalized.hours, minutes: normalized.minutes });
                              if (typeof onRowsChanged === "function") onRowsChanged();
                            }}
                            onDown={() => {
                              const current = parseInt(row.hours || "0", 10) || 0;
                              const dec = Math.max(0, current - 1);
                              const normalized = normalizeHoursMinutes(String(dec), row.minutes);
                              updateRow(row.id, { hours: normalized.hours, minutes: normalized.minutes });
                              if (typeof onRowsChanged === "function") onRowsChanged();
                            }}
                          />
                        </div>
                        <div className="text-[10px] text-red-600 mt-2 min-h-[14px]">{errors?.rows?.[errIdx]?.hours || ""}</div>
                      </div>
                    </div>

                    <div className="col-span-2 py-3 pt-5 pb-2 text-primary">
                      <div className="flex flex-col w-full h-full">
                        <div className="flex items-center">
                          <Input
                            value={row.minutes}
                            onChange={(e) => {
                              const minutesNext = sanitizeIntInput(e.target.value);
                              updateRow(row.id, { minutes: minutesNext });
                              pruneEmptyRows();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addRow();
                              }
                            }}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="0"
                            className="text-black w-full"
                          />
                          <SpinnerControls
                            disabled={false}
                            onUp={() => {
                              const current = parseInt(row.minutes || "0", 10) || 0;
                              const next = current + 1;
                              updateRow(row.id, { minutes: String(next) });
                              if (typeof onRowsChanged === "function") onRowsChanged();
                            }}
                            onDown={() => {
                              const current = parseInt(row.minutes || "0", 10) || 0;
                              const next = Math.max(0, current - 1);
                              updateRow(row.id, { minutes: String(next) });
                              if (typeof onRowsChanged === "function") onRowsChanged();
                            }}
                          />
                        </div>
                        <div className="text-[10px] text-red-600 mt-2 min-h-[14px]">{errors?.rows?.[errIdx]?.minutes || ""}</div>
                      </div>
                    </div>

                    <div className="col-span-2 py-3 pr-3 pt-5 pb-2 text-primary">
                      <div className="flex flex-col w-full h-full">
                        <Input
                          value={row.rate}
                          onChange={(e) => {
                            const next = sanitizeNumberInput(e.target.value);
                            updateRow(row.id, { rate: next });
                            pruneEmptyRows();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addRow();
                            }
                          }}
                          inputMode="decimal"
                          pattern="[0-9]*[.]?[0-9]*"
                          placeholder="0.00"
                          className="text-black w-full"
                        />
                        <div className="text-[10px] text-red-600 mt-2 min-h-[14px]">{errors?.rows?.[errIdx]?.rate || ""}</div>
                      </div>
                    </div>

                    <div className="col-span-2 p-3 pt-5 pb-2 text-sm font-bold text-[#2F7F57]">
                      <div className="flex items-center h-full pb-5">{money(amt)}</div>
                    </div>

                    <div className="col-span-1 py-3 pt-5 pb-2 pr-1 text-primary">
                      <div className="flex items-center pb-5 h-full">
                        <Trash2
                          className="h-5 w-5 cursor-pointer"
                          onClick={() => removeRow(row.id)}
                          aria-label="Remove row"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
